import { Directive, EventEmitter, HostListener, Output, Renderer2, inject, QueryList, ContentChildren } from '@angular/core';

import { ReOrderableItemDirective } from './reorderable-item.directive';

export type ReorderEvent = { fromIndex: number; toIndex: number };

@Directive({
  selector: '[nrcReorder]',
  standalone: true
})
export class ReorderDirective {
  @Output() public reorderEvent = new EventEmitter<ReorderEvent>();

  @ContentChildren(ReOrderableItemDirective) public reOrderableItems!: QueryList<ReOrderableItemDirective>;

  private readonly renderer = inject(Renderer2);
  private isReOrderable = false;
  private fromIndex = -1;
  private selectedElement: HTMLElement | null = null;
  private dragPreviewElement: HTMLElement | null = null;
  private dragPreviewOffsetX = 0;
  private dragPreviewOffsetY = 0;
  private dropTargetIndex = -1;
  private previousUserSelect: string | null = null;

  /**
   * @constructor
   */
  constructor() {
    this.onMousedown = this.onMousedown.bind(this);
    this.onMousemove = this.onMousemove.bind(this);
    this.onMouseup = this.onMouseup.bind(this);
  }

  /**
   * Handle mousedown event to start reordering.
   *
   * @param event - Mouse event.
   */
  @HostListener('mousedown', ['$event'])
  private onMousedown(event: MouseEvent): void {
    this.isReOrderable = true;

    // Prevent text selection during drag
    this.previousUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';

    const selectedIndex = this.findSelectedIndex(event.clientX, event.clientY);

    if (selectedIndex !== -1) {
      this.initializeDrag(selectedIndex, event);
    }
  }

  /**
   * Find the index of clicked element.
   *
   * @param clientX - Mouse X position.
   * @param clientY - Mouse Y position.
   *
   * @returns Selected element index.
   */
  private findSelectedIndex(clientX: number, clientY: number): number {
    return this.reOrderableItems.toArray().findIndex((item) => {
      const elementRef = item.elementRef.nativeElement;
      const boundingRect = elementRef.getBoundingClientRect();

      return clientX > boundingRect.left &&
        clientX < boundingRect.right &&
        clientY > boundingRect.top &&
        clientY < boundingRect.bottom;
    });
  }

  /**
   * Initialize drag state.
   *
   * @param selectedIndex - Index of selected item.
   * @param event
   */
  private initializeDrag(selectedIndex: number, event: MouseEvent): void {
    this.selectedElement = this.reOrderableItems.get(selectedIndex)?.elementRef.nativeElement ?? null;

    if (this.selectedElement) {
      this.fromIndex = selectedIndex;
      this.dropTargetIndex = selectedIndex;
      this.renderer.addClass(this.selectedElement, 'z-[1000]');
      this.renderer.addClass(this.selectedElement, 'opacity-80');
      this.renderer.addClass(this.selectedElement, 'shadow-lg');
      this.createDragPreview(this.selectedElement, event);
    }

    document.addEventListener('mousemove', this.onMousemove);
    document.addEventListener('mouseup', this.onMouseup);
  }

  /**
   * Creates a visual preview of the dragged element.
   *
   * @param element - The element being dragged.
   * @param event - The mouse event initiating the drag.
   */
  private createDragPreview(element: HTMLElement, event: MouseEvent): void {
    const rect = element.getBoundingClientRect();

    const clone = element.cloneNode(true) as HTMLElement;

    this.setupDragPreviewStyles(clone, rect);
    this.attachDragPreviewToDOM(clone, event, rect);
  }

  /**
   * Handle mousemove event during drag.
   *
   * @param event - Mouse event.
   */
  private onMousemove(event: MouseEvent): void {
    if (!this.isReOrderable || !this.selectedElement || !this.dragPreviewElement) {
      return;
    }

    // Move the preview with the mouse
    this.dragPreviewElement.style.left = `${event.clientX - this.dragPreviewOffsetX}px`;
    this.dragPreviewElement.style.top = `${event.clientY - this.dragPreviewOffsetY}px`;

    // Find the closest drop index
    const reOrderableItemsArray = this.reOrderableItems.toArray();
    let closestIndex = this.fromIndex;
    let minDistance = Number.MAX_VALUE;

    reOrderableItemsArray.forEach((item, i) => {
      const el = item.elementRef.nativeElement;
      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const dx = event.clientX - centerX;
      const dy = event.clientY - centerY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < minDistance) {
        minDistance = dist;
        closestIndex = i;
      }

      this.renderer.removeClass(el, 'drag-over');
    });

    // Add drag-over class to the closest item. This is used to highlight the closest item.
    this.dropTargetIndex = closestIndex;

    if (reOrderableItemsArray[closestIndex]) {
      this.renderer.addClass(reOrderableItemsArray[closestIndex].elementRef.nativeElement, 'drag-over');
    }
  }

  /**
   * Handle mouseup event to end reordering.
   */
  private onMouseup(): void {
    if (!this.selectedElement || !this.isReOrderable) {
      this.removeDragPreview();
      this.cleanupDrag();
      this.resetState();

      return;
    }

    // Remove drag preview
    this.removeDragPreview();

    // Remove drag-over class
    this.reOrderableItems.forEach(item => {
      this.renderer.removeClass(item.elementRef.nativeElement, 'drag-over');
    });

    this.cleanupDrag();

    // Emit reorder event if the item has been moved to a different position.
    if (this.fromIndex !== this.dropTargetIndex && this.dropTargetIndex >= 0) {
      this.reorderEvent.emit({ fromIndex: this.fromIndex, toIndex: this.dropTargetIndex });
    }

    this.resetState();
  }

  /**
   * Removes the drag preview element from the DOM.
   */
  private removeDragPreview(): void {
    if (this.dragPreviewElement) {
      this.dragPreviewElement.remove();
      this.dragPreviewElement = null;
    }
  }

  /**
   * Cleans up drag state and restores styles after drag ends.
   */
  private cleanupDrag(): void {
    // Restore user-select after drag
    if (this.previousUserSelect !== null) {
      document.body.style.userSelect = this.previousUserSelect;
      this.previousUserSelect = null;
    }
    this.removeDragPreview();

    if (!this.selectedElement) {
      return;
    }

    this.renderer.removeStyle(this.selectedElement, 'transform');
    this.renderer.removeClass(this.selectedElement, 'z-[1000]');
    this.renderer.removeClass(this.selectedElement, 'opacity-80');
    this.renderer.removeClass(this.selectedElement, 'shadow-lg');

    document.removeEventListener('mousemove', this.onMousemove);
    document.removeEventListener('mouseup', this.onMouseup);

    this.reOrderableItems.forEach(item => {
      this.resetItemPosition(item.elementRef.nativeElement);
    });

    this.isReOrderable = false;
  }

  /**
   * Resets the directive's internal state after drag ends.
   */
  private resetState(): void {
    this.isReOrderable = false;
    this.selectedElement = null;
    this.fromIndex = -1;
  }

  /**
   * Resets the position and transition styles of a given element.
   *
   * @param element - The element to reset.
   */
  private resetItemPosition(element: HTMLElement): void {
    this.renderer.removeClass(element, 'transition-transform');
    this.renderer.removeClass(element, 'duration-200');
    this.renderer.removeClass(element, 'ease-in-out');
    this.renderer.removeStyle(element, 'transform');
  }

  /**
   * Setup the styles for the drag preview.
   *
   * @param clone
   * @param rect
   */
  private setupDragPreviewStyles(clone: HTMLElement, rect: DOMRect): void {
    // Setup the styles for the drag preview.
    clone.style.position = 'fixed';
    clone.style.pointerEvents = 'none';
    clone.style.left = `${rect.left}px`;
    clone.style.top = `${rect.top}px`;
    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
    clone.style.zIndex = '2000';
    clone.style.opacity = '0.95';

    // Add the drag-preview class to the clone.
    clone.classList.add('drag-preview');
  }

  /**
   * Attaches the drag preview to the DOM.
   *
   * @param clone
   * @param event
   * @param rect
   */
  private attachDragPreviewToDOM(clone: HTMLElement, event: MouseEvent, rect: DOMRect): void {
    document.body.appendChild(clone);
    this.dragPreviewElement = clone;
    this.dragPreviewOffsetX = event.clientX - rect.left;
    this.dragPreviewOffsetY = event.clientY - rect.top;
  }
}
